/* ============================================================================
   Protocol Selector — card-level AJAX add-to-cart + format-toggle swap
   ----------------------------------------------------------------------------
   Two responsibilities:

   1) Each card owns its own mini-form (action=/cart/add, id=<variantId>, qty=1).
      On submit we intercept, POST JSON to /cart/add.js, then notify the theme.

   2) The format toggle (Pulver / Kapseln above the size tiles) navigates
      between sibling products. Production path uses window.kiPdpSwap to
      fetch-and-swap the destination in place — no real page navigation,
      no unload, cart drawer / scroll position survive. Falls back to a
      real navigation if the swap module isn't loaded for any reason.

   Re-init contract:
     init() is exposed on window.kiProtocolSelector so the swap module can
     re-bind freshly-swapped DOM. The form/toggle setup uses data-bound
     guards (data-ps-bound) so re-running init() on already-bound nodes
     is a safe no-op.

   Event flow (cart):
     1. Intercept submit → POST to /cart/add.js
     2. On success:
        - Dispatch 'cart:refresh' and 'cart:add' (common theme events)
        - Dispatch a native 'shopify:section:cart-updated' style event
        - As a last resort, if a cart drawer listens for 'ajaxProduct:added'
          (various themes), fire that too
        - If no drawer is detected within 400ms, open /cart as fallback
     3. On failure: show inline error below the card CTA
   ============================================================================ */

(function () {
  'use strict';

  var SECTION_SELECTOR = '.protocol-selector';
  var FORM_SELECTOR = '[data-ps-form]';
  var CTA_SELECTOR = '[data-ps-cta]';
  var FALLBACK_TIMEOUT_MS = 400;

  // Track which sibling URLs we've already prefetched in this page session.
  // Prevents redundant fetches if both desktop hover AND viewport-entry
  // fire (no harm — browser would dedupe — but cleaner not to issue them).
  var prefetched = {};

  function init() {
    var sections = document.querySelectorAll(SECTION_SELECTOR);
    sections.forEach(setupSection);
  }

  function setupSection(section) {
    var forms = section.querySelectorAll(FORM_SELECTOR);
    forms.forEach(setupForm);
    setupFormatToggle(section);
    setupViewportPrefetch(section);
  }

  // ── Format toggle (Pulver / Kapseln) ──────────────────────────────────
  // Three paths:
  //
  // (1) PRODUCTION SWAP (preferred): each tile has data-sibling-href and
  //     window.kiPdpSwap is loaded. Click triggers fetch-and-swap — the
  //     page never unloads, only #main-content + <head> + <body> attrs
  //     change. Fast, premium feel, cart state preserved.
  //
  // (2) PRODUCTION NAV (fallback): tile has data-sibling-href but the
  //     swap module didn't load. Click does window.location.href — same
  //     as the previous Option 1 implementation. Customer reaches the
  //     destination, just with a normal nav.
  //
  // (3) PREVIEW / IN-PLACE: no sibling href present at all. Swap variant
  //     data using data-variant-{powder,capsules} attrs on each card.
  //     Used by the local preview environment that doesn't have real
  //     Shopify URLs behind it.
  function setupFormatToggle(section) {
    var group = section.querySelector('.ps-format');
    if (!group || group.dataset.psBound === '1') return;
    group.dataset.psBound = '1';
    var inner = group.querySelector('.ps-format__group');
    var indicator = group.querySelector('.ps-format__indicator');
    var opts = group.querySelectorAll('.ps-format__opt');

    // Position the rose underline beneath the active tile via CSS custom
    // properties. Uses offsetLeft/offsetWidth (layout box, post-layout)
    // rather than getBoundingClientRect (which can be stale during font
    // swap / pre-grid-layout windows).
    function positionIndicator(animate) {
      if (!indicator || !inner) return;
      var active = inner.querySelector('.ps-format__opt.is-active');
      if (!active) return;
      var inset = 18;
      var left  = active.offsetLeft + inset;
      var width = active.offsetWidth - (inset * 2);
      // Sanity guard — if the tile reports an intrinsic-content width
      // (grid hasn't finalized), skip rather than draw a stub. Half the
      // group is the floor for a reasonable tile width.
      if (width < (inner.offsetWidth / 2) - 60) return;
      if (!animate) {
        indicator.style.transition = 'none';
        indicator.style.setProperty('--ps-indicator-left',  left  + 'px');
        indicator.style.setProperty('--ps-indicator-width', width + 'px');
        // Force reflow then re-enable transitions.
        // eslint-disable-next-line no-unused-expressions
        indicator.offsetWidth;
        indicator.style.transition = '';
      } else {
        indicator.style.setProperty('--ps-indicator-left',  left  + 'px');
        indicator.style.setProperty('--ps-indicator-width', width + 'px');
      }
    }

    // First-paint position (no animation). The CSS fallback values are
    // already correct for the first tile, so this is a refinement, not
    // a critical render.
    positionIndicator(false);

    // Reposition when the group's layout box changes (window resize,
    // font load, container query parent changing, etc).
    if (typeof ResizeObserver !== 'undefined' && inner) {
      var ro = new ResizeObserver(function () { positionIndicator(false); });
      ro.observe(inner);
    }
    // Fonts can shift tile widths after first paint when Cormorant
    // metrics swap in over the system fallback.
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(function () { positionIndicator(false); });
    }
    // Window resize as a final safety net.
    window.addEventListener('resize', function () { positionIndicator(false); });

    opts.forEach(function (opt) {
      // ── Click: commit the format choice ──
      opt.addEventListener('click', function () {
        var fmt = opt.getAttribute('data-format');
        if (!fmt) return;
        if (opt.classList.contains('is-active')) return; // no-op if active

        var siblingUrl = opt.getAttribute('data-sibling-href');

        // Path 1: fetch-and-swap if module is available.
        if (siblingUrl && window.kiPdpSwap && typeof window.kiPdpSwap.swap === 'function') {
          // Visual feedback BEFORE the fetch resolves. The toggle responds
          // immediately even if the network takes 800ms+ — the customer
          // experiences the click as instant. The actual DOM swap (which
          // re-renders the toggle from server HTML) lands when fetch
          // returns; the new render carries the correct active state, so
          // these classes get replaced rather than needing manual reset.
          opts.forEach(function (o) {
            var active = o === opt;
            o.classList.toggle('is-active', active);
            o.setAttribute('aria-checked', active ? 'true' : 'false');
          });
          if (inner) inner.setAttribute('data-active-format', fmt);
          positionIndicator(true);
          opt.setAttribute('aria-busy', 'true');

          /* Optimistic body[data-format] flip — drives [data-format-only]
             CSS rules (Verzehrempfehlung in ki-product-facts, format-note
             crossfade in ki-protocol-selector.css) to react synchronously
             with the click instead of waiting for the fetch round-trip.

             Without this, format-aware copy can't begin animating until
             the network completes (~200–800ms), which leaves the underline
             indicator animating alone while the rest of the format-aware
             UI sits frozen. With this, the body attribute flips at t=0
             and every CSS transition keyed to it starts in lockstep with
             the underline.

             Safe because:
               - On swap success, kiPdpSwap will set <body data-format>
                 to this same value when #main-content lands. No-op.
               - On swap failure, kiPdpSwap falls back to a real
                 navigation. The optimistic value doesn't matter
                 because the page unloads. */
          document.body.setAttribute('data-format', fmt);

          // Fade the grid during the swap (existing CSS hook).
          var grid = section.querySelector('.ps-grid');
          if (grid) grid.classList.add('is-swapping');

          // Persist the format intent — used by analytics and as a
          // diagnostic if a swap fails and the page eventually navigates.
          try { sessionStorage.setItem('ki:format-intent', fmt); } catch (e) { /* private mode */ }

          window.kiPdpSwap.swap(siblingUrl).then(function () {
            // Success or handled failure both end up here. The grid will
            // have been replaced by the swap, so removing is-swapping
            // here is a defensive no-op — but if the swap failed and
            // fell back to a nav, this line never runs anyway (page
            // unloads). On success, the new grid from the swapped DOM
            // doesn't carry is-swapping, so nothing to clean up either.
            // Kept for symmetry with the preview path below.
            if (grid && grid.isConnected) grid.classList.remove('is-swapping');
          });
          return;
        }

        // Path 2: real navigation fallback (Option 1 behavior).
        if (siblingUrl) {
          opt.setAttribute('aria-busy', 'true');
          opt.classList.add('is-loading');
          try { sessionStorage.setItem('ki:format-intent', fmt); } catch (e) { /* private mode */ }
          window.location.href = siblingUrl;
          return;
        }

        // Path 3: in-place preview swap.
        opts.forEach(function (o) {
          var active = o === opt;
          o.classList.toggle('is-active', active);
          o.setAttribute('aria-checked', active ? 'true' : 'false');
        });
        if (inner) inner.setAttribute('data-active-format', fmt);
        positionIndicator(true);
        applyFormat(section, fmt);
      });

      // ── Mouseenter (desktop): prefetch the sibling on hover ──
      // Hover is a high-confidence intent signal. The customer is reading
      // the tile and considering the click. Prefetch buys 100–500ms of
      // network time before the actual click fires.
      var siblingHref = opt.getAttribute('data-sibling-href');
      if (siblingHref) {
        opt.addEventListener('mouseenter', function () {
          maybePrefetch(siblingHref);
        });
        // Also prefetch on focus (keyboard navigation) — same intent,
        // different input modality.
        opt.addEventListener('focus', function () {
          maybePrefetch(siblingHref);
        });
      }
    });

    // Hash-arrival scrolling (#protocol-selector) is handled by an inline
    // <script> in ki-protocol-selector.liquid that runs at HTML parse time,
    // BEFORE the browser paints. Doing it here would be too late: this
    // file is loaded with `defer`, which runs after DOMContentLoaded —
    // by which point the hero image has already painted, causing a
    // visible scroll jump (the "flash"). The inline parser-time script
    // sidesteps that by scrolling synchronously the moment
    // #protocol-selector exists in the DOM, before the rest of the page
    // is parsed and painted.
  }

  // ── Viewport-entry prefetch (mobile-first, also fires on desktop) ──
  // Why IntersectionObserver instead of touchstart:
  //   touchstart fires during the tap, ~50–150ms before click. Not enough
  //   time on cellular for a fetch to complete. Also fires on accidental
  //   touches and scroll-brushes — lots of false positives.
  //
  //   Viewport entry is a much better mobile signal. By the time the
  //   protocol selector is on screen, the customer has scrolled past the
  //   hero and is in the consideration zone. They've committed to looking
  //   at format and price. Prefetching now means the fetch completes
  //   while they're still reading the size tiles — by the time they tap
  //   the format toggle, the response is already in cache.
  //
  // rootMargin: '200px' fires the prefetch when the selector is 200px
  // BELOW the fold, even earlier than visible entry. That's typically
  // when the customer is mid-headline-triad — buys another ~500ms of
  // network time before the toggle is even rendered to them.
  //
  // Fires once, then disconnects. If the customer triggers desktop hover
  // first, that wins; either way the URL ends up cached.
  function setupViewportPrefetch(section) {
    if (section.dataset.psPrefetchBound === '1') return;
    section.dataset.psPrefetchBound = '1';

    if (typeof IntersectionObserver === 'undefined') return; // very old browser
    if (!window.kiPdpSwap || typeof window.kiPdpSwap.prefetch !== 'function') return;

    // Find the inactive tile's sibling URL — that's what we'd prefetch.
    // Active tile has no data-sibling-href (it's the current page).
    var inactiveTile = section.querySelector('.ps-format__opt[data-sibling-href]');
    if (!inactiveTile) return;
    var url = inactiveTile.getAttribute('data-sibling-href');
    if (!url) return;

    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          maybePrefetch(url);
          io.disconnect();
          return;
        }
      }
    }, { rootMargin: '200px' });

    io.observe(section);
  }

  function maybePrefetch(url) {
    if (!url) return;
    if (prefetched[url]) return;
    prefetched[url] = true;
    if (window.kiPdpSwap && typeof window.kiPdpSwap.prefetch === 'function') {
      window.kiPdpSwap.prefetch(url);
    }
  }

  function applyFormat(section, fmt) {
    // Broadcast format choice globally so other PDP regions (Verzehr-
    // empfehlung, Nährwert footer, hero image, etc) can react via
    // [data-format-only] CSS rules.
    document.body.setAttribute('data-format', fmt);

    var grid = section.querySelector('.ps-grid');
    if (grid) {
      grid.classList.add('is-swapping');
      setTimeout(function () { grid.classList.remove('is-swapping'); }, 220);
    }
    var cards = section.querySelectorAll('.ps-card');
    cards.forEach(function (card) {
      var vId    = card.getAttribute('data-variant-' + fmt);
      var price  = card.getAttribute('data-price-'   + fmt);
      var permo  = card.getAttribute('data-permo-'   + fmt);
      var img    = card.getAttribute('data-img-'     + fmt);
      if (vId) {
        card.setAttribute('data-variant-id', vId);
        var idInput = card.querySelector('input[name="id"]');
        if (idInput) idInput.value = vId;
      }
      if (price) {
        var main = card.querySelector('.ps-card__price-main');
        if (main) main.textContent = price;
      }
      if (permo) {
        var pm = card.querySelector('.ps-card__permo');
        if (pm) pm.textContent = permo;
      }
      if (img) {
        var i = card.querySelector('.ps-card__img');
        if (i) i.src = img;
      }
    });
  }

  function setupForm(form) {
    if (form.dataset.psBound === '1') return;
    form.dataset.psBound = '1';

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      handleSubmit(form);
    });
  }

  function handleSubmit(form) {
    var cta = form.querySelector(CTA_SELECTOR);
    if (!cta) return;

    // Clear any previous error
    clearError(form);

    // Loading state
    cta.setAttribute('aria-busy', 'true');
    cta.disabled = true;

    var formData = new FormData(form);

    fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
      .then(function (response) {
        return response.json().then(function (body) {
          return { ok: response.ok, status: response.status, body: body };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          var msg = (result.body && (result.body.description || result.body.message)) || 'Could not add to cart.';
          throw new Error(msg);
        }
        onAddSuccess(form, result.body);
      })
      .catch(function (err) {
        showError(form, err && err.message ? err.message : 'Could not add to cart.');
      })
      .finally(function () {
        cta.removeAttribute('aria-busy');
        cta.disabled = false;
      });
  }

  function onAddSuccess(form, payload) {
    // Notify theme — fire a few common events so most cart drawers react.
    // `source` names the block that triggered the add: the PDP hero picker
    // (ki-product-detail.js) borrows handleSubmit via submitForm() below and
    // labels its form data-ps-source="product-detail", so listeners can tell
    // the two add-to-carts apart.
    var detail = { item: payload, source: form.dataset.psSource || 'protocol-selector' };

    dispatch(document, 'cart:refresh', detail);
    dispatch(document, 'cart:add', detail);
    dispatch(document, 'cart:updated', detail);
    dispatch(document, 'ajaxProduct:added', detail);

    // Also refetch cart so any theme listening on the cart-updated section event sees it
    fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart.js', {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        dispatch(document, 'cart:fetched', { cart: cart, source: 'protocol-selector' });
      })
      .catch(function () { /* non-fatal */ });

    // Fallback: if no cart drawer opens (nothing reacts), navigate to /cart
    var drawerOpened = watchForDrawerOpen(FALLBACK_TIMEOUT_MS);

    drawerOpened.then(function (opened) {
      if (!opened) {
        window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart';
      }
    });
  }

  function watchForDrawerOpen(timeoutMs) {
    return new Promise(function (resolve) {
      var drawerSelectors = [
        'cart-drawer',
        '.cart-drawer',
        '#CartDrawer',
        '.drawer--cart',
        '.js-drawer--cart',
        '#cart-notification',
        'cart-notification'
      ];

      var check = function () {
        for (var i = 0; i < drawerSelectors.length; i++) {
          var el = document.querySelector(drawerSelectors[i]);
          if (!el) continue;
          if (
            el.classList.contains('is-open') ||
            el.classList.contains('open') ||
            el.classList.contains('active') ||
            el.getAttribute('open') !== null ||
            el.getAttribute('aria-hidden') === 'false'
          ) {
            return true;
          }
        }
        return false;
      };

      if (check()) return resolve(true);

      var done = false;
      var observer = new MutationObserver(function () {
        if (done) return;
        if (check()) {
          done = true;
          observer.disconnect();
          resolve(true);
        }
      });

      observer.observe(document.body, {
        attributes: true,
        subtree: true,
        attributeFilter: ['class', 'open', 'aria-hidden']
      });

      setTimeout(function () {
        if (done) return;
        done = true;
        observer.disconnect();
        resolve(false);
      }, timeoutMs);
    });
  }

  function showError(form, message) {
    var errEl = form.querySelector('.ps-card__error');
    if (!errEl) {
      errEl = document.createElement('div');
      errEl.className = 'ps-card__error';
      errEl.style.cssText = [
        'margin-top: 10px',
        'font-family: var(--ps-font-sans, Inter, sans-serif)',
        'font-size: 11px',
        'color: #b00020',
        'text-align: center',
        'letter-spacing: 0.04em'
      ].join(';');
      errEl.setAttribute('role', 'alert');
      form.appendChild(errEl);
    }
    errEl.textContent = message;
  }

  function clearError(form) {
    var errEl = form.querySelector('.ps-card__error');
    if (errEl) errEl.remove();
  }

  function dispatch(target, name, detail) {
    try {
      target.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
    } catch (e) {
      var ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(name, true, false, detail);
      target.dispatchEvent(ev);
    }
  }

  // Expose init for re-binding after a fetch-and-swap.
  // Published BEFORE the initial DOMContentLoaded run so the swap module
  // can find it on its first call (which happens only after a user gesture,
  // long after this file has executed — but defensive ordering is cheap).
  window.kiProtocolSelector = window.kiProtocolSelector || {};
  window.kiProtocolSelector.init = init;

  // Shared add-to-cart, exposed for the PDP hero buy form (eCommerce layout).
  // That form lives outside .protocol-selector so init() never sees it, but it
  // should behave identically once submitted: AJAX POST → theme cart events →
  // drawer, or /cart as the fallback. Handing the form here keeps ONE cart
  // implementation instead of a second copy in ki-product-detail.js. The form
  // must contain a [data-ps-cta] submit button (the busy/disabled target).
  window.kiProtocolSelector.submitForm = handleSubmit;

  // Initial bootstrap. The old one-shot guard is gone — init() is now
  // re-entrant-safe via the data-ps-bound flags inside.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.matches && e.target.matches(SECTION_SELECTOR + ', ' + SECTION_SELECTOR + ' *')) {
      init();
    }
  });
})();